import { Link } from 'react-router-dom';
import type { HabitaProcessListItem } from '../../../shared/models';
import {
  DocumentProgress,
  HabitaButton,
  HabitaDateRangeFilter,
  HabitaEmptyState,
  HabitaSearchInput,
  HabitaSelect,
  HabitaTextInput,
  PastaStatusBadge,
  classNames,
  habitaControlBorderClass,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import {
  createPastaDetailNavigationState,
  createPastaDetailPath,
  rememberPastaDetailExternalIdsFromItem,
} from '../../../shared/navigation/pastaDetailNavigation';
import { apiStatusToPastaStatus } from '../../../shared/habitaStatusPresentation';
import {
  formatCPF,
  formatHabitaCreationTime,
  formatHabitaDate,
  formatHabitaDateTime,
  processUpdatedAtOf,
} from '../../../shared/utils';
import {
  processSortOrderLabel,
  type ProcessSortOrder,
} from '../../../shared/utils/processOrdering';
import type { HabitaDateRangeFilterValue } from '../../../shared/components/habita';

export type PastasViewMode = 'tabela' | 'cards';

const rowHoverClass = 'hover:bg-accent';

function nameOf(item: HabitaProcessListItem): string {
  return item.customer_name || item.name || `Processo #${item.id}`;
}

function cpfOf(item: HabitaProcessListItem): string {
  return formatCPF(item.customer_cpf ?? '') || '-';
}

function responsavelOf(item: HabitaProcessListItem): string {
  return item.responsible_name?.trim() || item.responsible_email?.trim() || '-';
}

function fileCountOf(item: HabitaProcessListItem): number {
  return Number(item.file_count ?? 0);
}

function updatedAtOf(item: HabitaProcessListItem): string {
  return processUpdatedAtOf(item);
}

export function PastasToolbar({
  dateFilter,
  hasFilters,
  maxDateInput,
  onClearFilters,
  onDateFilterApply,
  onDateFilterClear,
  onProcessoChange,
  onResponsavelChange,
  searchTerm,
  processoTerm,
  responsavel,
  responsaveis,
  resultCount,
  resultCountLabel,
  showResponsavelFilter = true,
  status,
  statusOptions,
  onStatusChange,
  sortOrder,
  onSortOrderChange,
  viewMode,
  onSearchChange,
  onViewModeChange,
}: {
  dateFilter: HabitaDateRangeFilterValue;
  hasFilters: boolean;
  maxDateInput?: string;
  onClearFilters: () => void;
  onDateFilterApply: (value: HabitaDateRangeFilterValue) => void;
  onDateFilterClear: () => void;
  onProcessoChange: (value: string) => void;
  onResponsavelChange: (value: string) => void;
  searchTerm: string;
  processoTerm: string;
  responsavel: string;
  responsaveis: { label: string; value: string }[];
  resultCount: number;
  resultCountLabel?: string;
  showResponsavelFilter?: boolean;
  status: string;
  statusOptions: { label: string; value: string }[];
  onStatusChange: (value: string) => void;
  sortOrder: ProcessSortOrder;
  onSortOrderChange: (value: ProcessSortOrder) => void;
  viewMode: PastasViewMode;
  onSearchChange: (value: string) => void;
  onViewModeChange: (value: PastasViewMode) => void;
}) {
  const toggleButtonClass =
    'flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors';

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <HabitaSearchInput
          value={searchTerm}
          onValueChange={onSearchChange}
          placeholder="Buscar por cliente, CPF ou documento"
          containerClassName="md:max-w-md"
          inputClassName="bg-card text-textPrimary"
        />

        <div className="flex items-center gap-3">
          <div className={`inline-flex rounded-md border ${habitaControlBorderClass} bg-card p-0.5`}>
            <button
              type="button"
              onClick={() => onViewModeChange('tabela')}
              aria-label="Visualizar como tabela"
              className={
                viewMode === 'tabela'
                  ? `${toggleButtonClass} bg-primary text-primary-foreground`
                  : `${toggleButtonClass} text-textSecondary hover:bg-accent hover:text-textPrimary`
              }
            >
              <i className="ri-list-check text-lg" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => onViewModeChange('cards')}
              aria-label="Visualizar como cards"
              className={
                viewMode === 'cards'
                  ? `${toggleButtonClass} bg-primary text-primary-foreground`
                  : `${toggleButtonClass} text-textSecondary hover:bg-accent hover:text-textPrimary`
              }
            >
              <i className="ri-layout-grid-line text-lg" aria-hidden />
            </button>
          </div>

          <HabitaButton disabled icon="ri-add-line" variant="primary" title="Nova pasta em breve">
            Nova pasta
          </HabitaButton>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <HabitaDateRangeFilter
          value={dateFilter}
          onApply={onDateFilterApply}
          onClear={onDateFilterClear}
          maxDateInput={maxDateInput}
        />
        <HabitaTextInput
          value={processoTerm}
          onChange={(event) => onProcessoChange(event.target.value)}
          placeholder="Processo"
          containerClassName="w-40"
        />
        {showResponsavelFilter && (
          <HabitaSelect
            value={responsavel}
            onChange={(event) => onResponsavelChange(event.target.value)}
            options={[
              { label: 'Responsável', value: '__all__' },
              ...responsaveis,
            ]}
            className="w-48"
            aria-label="Responsável"
          />
        )}
        <HabitaSelect
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
          options={[{ label: 'Status', value: '__all__' }, ...statusOptions]}
          className="w-56"
          aria-label="Status"
        />
        <HabitaButton
          icon={sortOrder === 'desc' ? 'ri-sort-desc' : 'ri-sort-asc'}
          onClick={() => onSortOrderChange(sortOrder === 'desc' ? 'asc' : 'desc')}
          size="sm"
        >
          {processSortOrderLabel(sortOrder)}
        </HabitaButton>
        {hasFilters && (
          <HabitaButton icon="ri-close-line" onClick={onClearFilters} size="sm" variant="ghost">
            Limpar
          </HabitaButton>
        )}
        <span className="ml-auto font-instrument text-xs text-textSecondary">
          {resultCountLabel ?? resultCount} {resultCount === 1 ? 'pasta' : 'pastas'}
        </span>
      </div>
    </div>
  );
}

export function PastasTable({ items }: { items: HabitaProcessListItem[] }) {
  return (
    <div className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-left font-instrument text-sm">
          <thead className={`border-b ${habitaControlBorderClass} bg-muted text-xs text-textSecondary`}>
            <tr>
              <th className="px-4 py-2.5 font-medium">Processo</th>
              <th className="px-4 py-2.5 font-medium">Cliente</th>
              <th className="px-4 py-2.5 font-medium">CPF</th>
              <th className="px-4 py-2.5 font-medium">Responsável</th>
              <th className="px-4 py-2.5 font-medium">Documentos</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Tempo de criação</th>
              <th className="px-4 py-2.5 font-medium">Atualizado</th>
              <th className="px-4 py-2.5 font-medium" aria-label="Ações" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {items.map((item) => {
              const createdAt = formatHabitaCreationTime(item.created_at);

              return (
                <tr key={String(item.id)} className={`transition-colors ${rowHoverClass}`}>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-textSecondary">
                    #{item.id}
                  </td>
                  <td className="min-w-56 px-4 py-2.5 font-medium text-textPrimary">{nameOf(item)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-textSecondary">{cpfOf(item)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-textPrimary">{responsavelOf(item)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-textPrimary">{fileCountOf(item)}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <PastaStatusBadge status={apiStatusToPastaStatus(item.status)} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-textSecondary">
                    <span title={createdAt.title}>
                      {createdAt.label}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-textSecondary">
                    {formatHabitaDate(updatedAtOf(item))}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right">
                    <Link
                      to={createPastaDetailPath(item)}
                      state={createPastaDetailNavigationState('pastas', item)}
                      onClick={() => rememberPastaDetailExternalIdsFromItem(item)}
                      className="rounded-md px-3 py-1.5 font-instrument text-xs font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary"
                    >
                      Abrir
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PastasCardGrid({ items }: { items: HabitaProcessListItem[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {items.map((item) => {
        const createdAt = formatHabitaCreationTime(item.created_at);

        return (
          <Link
            key={String(item.id)}
            to={createPastaDetailPath(item)}
            state={createPastaDetailNavigationState('pastas', item)}
            onClick={() => rememberPastaDetailExternalIdsFromItem(item)}
            className={classNames(habitaSurfaceClass, 'p-4 transition-colors hover:border-primary/70 sm:p-5')}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-[11px] text-textSecondary">#{item.id}</p>
                <h2 className="mt-1 truncate font-instrument text-sm font-semibold text-textPrimary">
                  {nameOf(item)}
                </h2>
                <p className="mt-0.5 font-instrument text-xs text-textSecondary">{cpfOf(item)}</p>
              </div>
              <PastaStatusBadge status={apiStatusToPastaStatus(item.status)} />
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 font-instrument text-xs text-textSecondary">
              <span>{fileCountOf(item)} documentos</span>
              <span className="truncate">{responsavelOf(item)}</span>
            </div>

            <div className="mt-1 flex items-center justify-between gap-3 font-instrument text-[11px] text-textSecondary">
              <span title={createdAt.title}>
                {createdAt.label}
              </span>
              <span>Atualizado {formatHabitaDateTime(updatedAtOf(item))}</span>
            </div>

            <div className="mt-3">
              <DocumentProgress
                total={item.file_count ?? undefined}
                validados={item.completed_file_count ?? undefined}
                reprovados={item.failed_file_count ?? undefined}
              />
            </div>
          </Link>
        );
      })}
    </div>
  );
}

export function PastasEmptyState({ searchTerm }: { searchTerm: string }) {
  return (
    <HabitaEmptyState
      icon="ri-folder-search-line"
      title="Nenhuma pasta encontrada"
      description={
        searchTerm.trim()
          ? 'Tente buscar por outro nome ou CPF.'
          : 'As pastas aparecerão aqui quando disponíveis.'
      }
    />
  );
}

export function PastasLoadingState() {
  return (
    <div className={classNames(habitaSurfaceClass, 'overflow-hidden')}>
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex animate-pulse items-center gap-4 border-b border-border-subtle px-4 py-3.5 last:border-b-0">
          <div className="h-4 w-20 rounded bg-muted" />
          <div className="h-4 flex-1 rounded bg-muted" />
          <div className="h-4 w-28 rounded bg-muted" />
          <div className="h-6 w-20 rounded bg-muted/70" />
        </div>
      ))}
    </div>
  );
}
